/**
 * SMS Opt-In Values Utility
 */

import { FieldEntityProps, FieldValueEntityProps } from '../types';

export interface SmsOptInValues {
  isSms: boolean | undefined;
  smsText: FieldValueEntityProps | undefined;
  smsCtaText: FieldValueEntityProps | undefined;
  smsCtaHref: string | undefined;
}

const DEFAULT_CTA_HREF = '/privacy-terms-cookies-policies/';

/**
 * Extracts SMS opt-in text and link values from field configuration.
 *
 * @param fields - Array of field entities to search for SMS opt-in field
 * @returns Object containing SMS text, CTA text, CTA href, and whether SMS is configured
 *
 * @example
 * const { smsText, smsCtaText, smsCtaHref } = getSmsOptInValues(fields);
 * // smsText.value = "By checking this box, I consent to..."
 * // smsCtaText.value = "Privacy Policy"
 * // smsCtaHref = "/privacy-terms-cookies-policies/"
 */
export const getSmsOptInValues = (
  fields: FieldEntityProps[],
): SmsOptInValues => {
  const smsOptField = fields.find(
    (field) => field.parameterName === 'sms_opt_in',
  );

  if (!smsOptField?.fieldValues) {
    return {
      isSms: undefined,
      smsText: undefined,
      smsCtaText: undefined,
      smsCtaHref: undefined,
    };
  }

  const smsText = smsOptField.fieldValues.find(
    (v) => v.valueType === 'sms_opt_in_text',
  );
  const smsCtaText = smsOptField.fieldValues.find(
    (v) => v.valueType === 'sms_opt_in_cta_text',
  );
  const smsCtaHrefValue = smsOptField.fieldValues.find(
    (v) => v.valueType === 'sms_opt_in_cta_href',
  );
  const smsCtaHref = smsCtaHrefValue?.value || DEFAULT_CTA_HREF;

  const isSms = !!(smsText && smsCtaText && smsCtaHref);

  return { isSms, smsText, smsCtaText, smsCtaHref };
};
